import React, { useState, useEffect, useContext } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
	TextField,
	Box,
	Alert,
	Card,
	CardContent,
	Grid,
	Stack,
	Chip,
	CircularProgress,
	IconButton,
	useTheme,
	Autocomplete
} from "@mui/material";
import {
	LocationOn as LocationOnIcon,
	Link as LinkIcon,
	Map as MapIcon,
	Close as CloseIcon,
	CheckCircle as CheckCircleIcon,
	Google as GoogleIcon,
	Business as BusinessIcon,
	Clear as ClearIcon
} from '@mui/icons-material';
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import ToastError from "../../toast/error/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const SendLocationModal = ({ title, open, onClose, onConfirm }) => {
	const theme = useTheme();
	const { user } = useContext(AuthContext);
	const [loading, setLoading] = useState(false);
	const [position, setPosition] = useState({});
	const [locationInfo, setLocationInfo] = useState({});
	const [editedName, setEditedName] = useState("");
	const [editedAddress, setEditedAddress] = useState("");
	const [mapLink, setMapLink] = useState("");
	const [resolvedUrl, setResolvedUrl] = useState("");
	const [error, setError] = useState("");
	
	// Estados para autocomplete de endereços da empresa
	const [companyAddresses, setCompanyAddresses] = useState([]);
	const [selectedCompanyAddress, setSelectedCompanyAddress] = useState(null);
	const [loadingAddresses, setLoadingAddresses] = useState(false);

	// Buscar endereços da empresa ao abrir o modal
	useEffect(() => {
		const fetchCompanyAddresses = async () => {
			if (!open || !user?.companyId) return;
			
			setLoadingAddresses(true);
			try {
				const { data } = await api.get("/companyAddresses");
				setCompanyAddresses(data.companyAddresses || []);
			} catch (error) {
				console.error("Erro ao buscar endereços da empresa:", error);
				ToastError("Erro ao carregar endereços da empresa");
			} finally {
				setLoadingAddresses(false);
			}
		};

		fetchCompanyAddresses();
	}, [open, user?.companyId]);

	// Função para selecionar endereço da empresa
	const handleSelectCompanyAddress = (event, newValue) => {
		setSelectedCompanyAddress(newValue);
		
		if (newValue) {
			// Preencher campos com dados do endereço selecionado
			const lat = newValue.lat ? parseFloat(newValue.lat) : null;
			const lng = newValue.lng ? parseFloat(newValue.lng) : null;
			
			// Só definir posição se tiver coordenadas válidas
			if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
				setPosition({ lat, lng });
				setLocationInfo({
					name: newValue.name || '',
					address: newValue.address || '',
					coordinates: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
				});
			} else {
				// Se não tem coordenadas, limpar posição mas manter info
				setPosition({});
				setLocationInfo({
					name: newValue.name || '',
					address: newValue.address || '',
					coordinates: ''
				});
			}
			
			setEditedName(newValue.name || '');
			setEditedAddress(newValue.address || '');
			setResolvedUrl(newValue.mapsUrl || "");
			setMapLink(newValue.mapsUrl || "");
			setError("");
			
			const hasCoords = lat && lng && !isNaN(lat) && !isNaN(lng);
			const message = hasCoords 
				? `Endereço "${newValue.name}" selecionado com localização!`
				: `Endereço "${newValue.name}" selecionado (sem coordenadas)`;
				
			toast.success(message, {
				position: "bottom-right"
			});
		}
	};

	// Função para limpar seleção
	const handleClearSelection = () => {
		setSelectedCompanyAddress(null);
		setPosition({});
		setLocationInfo({});
		setEditedName("");
		setEditedAddress("");
		setMapLink("");
		setResolvedUrl("");
		setError("");
		
		toast.info("Seleção removida", {
			position: "bottom-right"
		});
	};

	// Função para resolver URL usando o backend
	const resolveGoogleMapsUrl = async (url) => {
		try {
			const response = await api.get('/google/resolve-url', {
				params: { url }
			});
			return response.data;
		} catch (error) {
			console.error('Erro ao resolver URL:', error);
			throw error;
		}
	};

	// Função para processar links do Google Maps
	const processMapLink = async () => {
		if (!mapLink.trim()) {
			ToastError("Cole um link válido do Google Maps.");
			return;
		}

		setLoading(true);
		setError("");
		
		try {
			// Usar o backend para resolver a URL e extrair coordenadas
			const result = await resolveGoogleMapsUrl(mapLink.trim());
			
			setPosition({ lat: result.lat, lng: result.lng });
			setResolvedUrl(result.resolvedUrl || mapLink);
			const name = result.name || result.address?.split(',')[0] || "Localização do Google Maps";
			const address = result.address || `Coordenadas: ${result.lat.toFixed(6)}, ${result.lng.toFixed(6)}`;
			
			setLocationInfo({
				name: name,
				address: address,
				coordinates: `${result.lat.toFixed(6)}, ${result.lng.toFixed(6)}`
			});
			
			setEditedName(name);
			setEditedAddress(address);
			
			toast.success("Localização extraída com sucesso!", {
				position: "bottom-right"
			});
		} catch (error) {
			console.error("Erro ao processar link:", error);
			const errorMessage = error.response?.data?.error || "Não foi possível extrair coordenadas do link. Verifique se é um link válido do Google Maps.";
			setError(errorMessage);
			ToastError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	// Função para gerar URL do Google Maps iframe
	const generateGoogleMapsEmbedUrl = (lat, lng) => {
		const isDarkMode = theme.palette.mode === 'dark';
		
		// Parâmetros base
		let url = `https://maps.google.com/maps?q=${lat},${lng}&hl=pt-BR&z=16&output=embed`;
		
		// Adicionar parâmetros de tema escuro
		if (isDarkMode) {
			url += '&theme=dark&style=feature:all|element:geometry|color:0x242f3e';
			url += '&style=feature:all|element:labels.text.stroke|color:0x242f3e';
			url += '&style=feature:all|element:labels.text.fill|color:0x746855';
			url += '&style=feature:administrative.locality|element:labels.text.fill|color:0xd59563';
			url += '&style=feature:poi|element:labels.text.fill|color:0xd59563';
			url += '&style=feature:poi.park|element:geometry|color:0x263c3f';
			url += '&style=feature:poi.park|element:labels.text.fill|color:0x6b9a76';
			url += '&style=feature:road|element:geometry|color:0x38414e';
			url += '&style=feature:road|element:geometry.stroke|color:0x212a37';
			url += '&style=feature:road|element:labels.text.fill|color:0x9ca5b3';
			url += '&style=feature:road.highway|element:geometry|color:0x746855';
			url += '&style=feature:road.highway|element:geometry.stroke|color:0x1f2835';
			url += '&style=feature:road.highway|element:labels.text.fill|color:0xf3d19c';
			url += '&style=feature:transit|element:geometry|color:0x2f3948';
			url += '&style=feature:transit.station|element:labels.text.fill|color:0xd59563';
			url += '&style=feature:water|element:geometry|color:0x17263c';
			url += '&style=feature:water|element:labels.text.fill|color:0x515c6d';
			url += '&style=feature:water|element:labels.text.stroke|color:0x17263c';
		}
		
		return url;
	};

	// Função para enviar localização
	const sendLocationMessage = () => {
		if (!position.lat || !position.lng) {
			ToastError('Extraia as coordenadas de um link do Google Maps primeiro.');
			return;
		}

		const locationData = {
			lat: position.lat,
			lng: position.lng,
			info: {
				name: editedName || locationInfo.name,
				address: editedAddress || locationInfo.address,
				coordinates: `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`
			}
		};

		onConfirm(locationData);
		handleClose();

		toast.success("Localização enviada com sucesso!", {
			position: "bottom-right"
		});
	};

	// Limpar estado
	const cleanup = () => {
		setPosition({});
		setLocationInfo({});
		setEditedName("");
		setEditedAddress("");
		setMapLink("");
		setResolvedUrl("");
		setError("");
		setSelectedCompanyAddress(null);
		setCompanyAddresses([]);
	};

	const handleClose = () => {
		cleanup();
		onClose(false);
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			aria-labelledby="location-dialog"
			maxWidth="md"
			fullWidth
			BackdropProps={{
				style: {
					backdropFilter: 'blur(4px)',
					backgroundColor: 'rgba(0, 0, 0, 0.5)',
				},
			}}
			PaperProps={{
				sx: {
					borderRadius: 3,
					maxHeight: '90vh'
				}
			}}
		>
			<DialogTitle>
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Stack direction="row" alignItems="center" spacing={2}>
						<LocationOnIcon color="primary" sx={{ fontSize: 32 }} />
						<Typography variant="h5" fontWeight={600}>
							{title || "Enviar Localização"}
						</Typography>
					</Stack>
					
					<Stack direction="row" spacing={1}>
						<Chip 
							icon={<GoogleIcon />}
							label="Google Maps"
							size="small"
							color="primary"
							variant="outlined"
						/>
						<IconButton onClick={handleClose} size="small">
							<CloseIcon />
						</IconButton>
					</Stack>
				</Stack>
			</DialogTitle>

			<DialogContent dividers sx={{ p: 3 }}>
				<Grid container spacing={3}>
					{/* Autocomplete de Endereços da Empresa */}
					<Grid item xs={12}>
						<Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'primary.50' }}>
							<CardContent>
								<Stack spacing={2}>
									<Stack direction="row" alignItems="center" spacing={1}>
										<BusinessIcon color="primary" />
										<Typography variant="h6">Endereços da Empresa</Typography>
										{selectedCompanyAddress && (
											<Chip 
												label="Selecionado" 
												color="success" 
												variant="outlined"
												icon={<CheckCircleIcon />}
												size="small"
											/>
										)}
									</Stack>
									<Typography variant="body2" color="text.secondary">
										{companyAddresses.length > 0 
											? "Selecione um endereço já cadastrado na empresa ou digite um novo link do Google Maps"
											: "Nenhum endereço cadastrado ainda. Use o link do Google Maps abaixo para criar uma nova localização."
										}
									</Typography>
									
									{companyAddresses.length === 0 && !loadingAddresses && (
										<Alert severity="warning" sx={{ borderRadius: 2 }}>
											<Typography variant="body2">
												<strong>💡 Dica:</strong> Cadastre endereços frequentes na seção "Endereços da Empresa" para reutilizá-los rapidamente.
											</Typography>
										</Alert>
									)}
									
									{(companyAddresses.length > 0 || loadingAddresses) && (
										<Stack direction="row" spacing={2} alignItems="center">
											<Autocomplete
												fullWidth
												options={companyAddresses}
												getOptionLabel={(option) => option.name || ''}
												value={selectedCompanyAddress}
												onChange={handleSelectCompanyAddress}
												loading={loadingAddresses}
												disabled={companyAddresses.length === 0 && !loadingAddresses}
												renderInput={(params) => (
													<TextField
														{...params}
														placeholder="Buscar endereço da empresa..."
														size="small"
														sx={{ 
															'& .MuiOutlinedInput-root': { 
																borderRadius: 2 
															} 
														}}
														InputProps={{
															...params.InputProps,
															startAdornment: (
																<BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
															),
															endAdornment: (
																<>
																	{loadingAddresses ? <CircularProgress color="inherit" size={20} /> : null}
																	{params.InputProps.endAdornment}
																</>
															),
														}}
													/>
												)}
												renderOption={(props, option) => (
													<Box component="li" {...props}>
														<Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
															<LocationOnIcon color="primary" fontSize="small" />
															<Stack sx={{ flex: 1 }}>
																<Typography variant="body1" fontWeight={600}>
																	{option.name}
																</Typography>
																<Typography variant="caption" color="text.secondary" noWrap>
																	{option.address}
																</Typography>
															</Stack>
															{option.lat && option.lng && (
																<Chip 
																	label="Com coordenadas" 
																	size="small" 
																	color="success" 
																	variant="outlined"
																/>
															)}
														</Stack>
													</Box>
												)}
												noOptionsText="Nenhum endereço encontrado"
												loadingText="Carregando endereços..."
											/>
											
											{selectedCompanyAddress && (
												<Button
													variant="outlined"
													color="secondary"
													startIcon={<ClearIcon />}
													onClick={handleClearSelection}
													sx={{ borderRadius: 2, minWidth: 100 }}
												>
													Limpar
												</Button>
											)}
										</Stack>
									)}
								</Stack>
							</CardContent>
						</Card>
					</Grid>

					{/* Link do Google Maps */}
					<Grid item xs={12}>
						<Card variant="outlined" sx={{ borderRadius: 2 }}>
							<CardContent>
								<Stack spacing={2}>
									<Stack direction="row" alignItems="center" spacing={1}>
										<LinkIcon color="primary" />
										<Typography variant="h6">Link do Google Maps</Typography>
									</Stack>
									<Typography variant="body2" color="text.secondary">
										Cole qualquer link do Google Maps (incluindo links encurtados goo.gl)
									</Typography>
									
									<Alert severity="info" sx={{ borderRadius: 2 }}>
										<Typography variant="body2">
											<strong>📍 Como obter:</strong> Abra o Google Maps → Clique no local → Compartilhar → Copiar link
										</Typography>
									</Alert>
									
									{error && (
										<Alert severity="error" sx={{ borderRadius: 2 }}>
											{error}
										</Alert>
									)}
									
									<Stack direction="row" spacing={2}>
										<TextField
											fullWidth
											placeholder="https://maps.app.goo.gl/1NaGCSJoXd4mi1xu8 ou https://maps.google.com/..."
											value={mapLink}
											onChange={(e) => setMapLink(e.target.value)}
											size="small"
											disabled={loading}
											sx={{ 
												'& .MuiOutlinedInput-root': { 
													borderRadius: 2 
												} 
											}}
										/>
										<Button
											variant="contained"
											startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
											onClick={processMapLink}
											disabled={loading || !mapLink.trim()}
											sx={{ borderRadius: 2, minWidth: 120 }}
										>
											{loading ? "..." : "Extrair"}
										</Button>
									</Stack>
								</Stack>
							</CardContent>
						</Card>
					</Grid>

					{/* Preview da Localização */}
					{(position.lat && position.lng) && (
						<Grid item xs={12}>
							<Card sx={{ borderRadius: 2 }}>
								<CardContent>
									<Stack spacing={3}>
										<Stack direction="row" alignItems="center" justifyContent="space-between">
											<Stack direction="row" alignItems="center" spacing={1}>
												<CheckCircleIcon color="success" />
												<Typography variant="h6">Localização Confirmada</Typography>
											</Stack>
											<Chip 
												label="Pronto para enviar" 
												color="success" 
												variant="outlined"
												icon={<CheckCircleIcon />}
											/>
										</Stack>
										
										{/* Informações da Localização */}
										<Box sx={{ 
											p: 2, 
											bgcolor: 'background.default', 
											borderRadius: 2,
											border: '1px solid',
											borderColor: 'divider'
										}}>
											{locationInfo.name && (
												<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
													<GoogleIcon color="primary" fontSize="small" />
													<TextField
														variant="outlined"
														size="small"
														value={editedName}
														onChange={(e) => setEditedName(e.target.value)}
														placeholder="Nome da localização"
														sx={{ 
															flex: 1,
															'& .MuiOutlinedInput-root': { 
																borderRadius: 1,
																fontWeight: 600,
																fontSize: '1.1rem'
															}
														}}
													/>
												</Stack>
											)}
											
											{locationInfo.address && (
												<Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
													<LocationOnIcon color="action" fontSize="small" sx={{ mt: 0.5 }} />
													<TextField
														variant="outlined"
														size="small"
														multiline
														rows={2}
														value={editedAddress}
														onChange={(e) => setEditedAddress(e.target.value)}
														placeholder="Endereço da localização"
														sx={{ 
															flex: 1,
															'& .MuiOutlinedInput-root': { 
																borderRadius: 1,
																color: 'text.secondary'
															}
														}}
													/>
												</Stack>
											)}
											
											<Stack direction="row" alignItems="center" spacing={1}>
												<LocationOnIcon color="action" fontSize="small" />
												<Typography variant="caption" color="text.secondary" fontFamily="monospace">
													Lat: {position.lat.toFixed(6)}, Lng: {position.lng.toFixed(6)}
												</Typography>
											</Stack>
										</Box>

										{/* Mapa Google Maps Iframe */}
										<Box>
											<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
												<MapIcon color="primary" />
												<Typography variant="subtitle1" fontWeight={600}>
													Preview do Mapa
												</Typography>
												<Chip label="Google Maps" size="small" variant="outlined" color="primary" />
											</Stack>
											
											<Box sx={{ 
												borderRadius: 2, 
												overflow: 'hidden', 
												boxShadow: 3, 
												height: 400,
												position: 'relative'
											}}>
												<iframe
													src={generateGoogleMapsEmbedUrl(position.lat, position.lng)}
													width="100%"
													height="100%"
													style={{ border: 0, pointerEvents: 'none' }}
													loading="lazy"
													referrerPolicy="no-referrer-when-downgrade"
													title="Google Maps Preview"
													sandbox="allow-scripts"
												/>
											</Box>
										</Box>
									</Stack>
								</CardContent>
							</Card>
						</Grid>
					)}
				</Grid>
			</DialogContent>

			<DialogActions sx={{ p: 3 }}>
				<Stack direction="row" spacing={2} sx={{ width: '100%' }}>
					<Button 
						variant="outlined" 
						onClick={handleClose}
						disabled={loading}
						size="large"
						sx={{ borderRadius: 2, minWidth: 120 }}
					>
						{i18n.t("confirmationModal.buttons.cancel") || "Cancelar"}
					</Button>
					
					<Button 
						variant="contained" 
						onClick={sendLocationMessage} 
						startIcon={<LocationOnIcon />}
						disabled={loading || !position.lat || !position.lng}
						size="large"
						fullWidth
						sx={{ 
							borderRadius: 2,
							bgcolor: 'success.main',
							'&:hover': {
								bgcolor: 'success.dark'
							}
						}}
					>
						{loading ? "Processando..." : "Enviar Localização"}
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};

export default SendLocationModal;